import { proethicaModuleListMeta } from "../praxis-proethica";
import {
	type RawProethicaSessionItem,
	buildSessionItems,
	extractModuleSummary,
} from "./proethica";
import {
	type NewFormatModuleItem,
	type NormalizeModuleOptions,
	PROETHICA_TEST_UI_TYPE,
	findModuleOverview,
	normalizeKnowledgeCheck,
	normalizeModuleItems,
} from "./proethica-adapter";

type BuildProethicaModuleParams = {
	raw: NewFormatModuleItem[];
	moduleLabel: string;
	normalizeOptions?: NormalizeModuleOptions;
};

/**
 * Shared pipeline for every proethics module: normalize new-format items,
 * inject module metadata onto the landing item, then build session items.
 */
export const buildProethicaModule = ({
	raw,
	moduleLabel,
	normalizeOptions,
}: BuildProethicaModuleParams) => {
	const meta = proethicaModuleListMeta[moduleLabel];
	const overview = findModuleOverview(raw);
	const normalizedItems = normalizeModuleItems(raw, normalizeOptions);

	const knowledgeCheckQuestionCount =
		overview?.knowledgeCheckCount ||
		normalizedItems.filter((item) => item.type === "MCQ").length;

	const itemsWithMeta: RawProethicaSessionItem[] = normalizedItems.map(
		(item) => {
			if (item.type !== "MODULE_LANDING") {
				return item;
			}

			return {
				...item,
				content: {
					...(item.content as object),
					module: {
						label: moduleLabel,
						title: overview?.title ?? meta.title,
						badgeSource: meta.thumbnailSource,
						durationLabel: overview
							? `${overview.estimatedDurationMinutes} min`
							: meta.durationLabel,
						activityCount: overview?.activityCount,
						knowledgeCheckQuestionCount,
					},
				},
			};
		},
	);

	return {
		normalizedItems,
		sessionItems: buildSessionItems(itemsWithMeta, { moduleLabel }),
		summary: extractModuleSummary(normalizedItems),
	};
};

// Pre/post tests are a flat MCQ sequence with none of the module scaffolding
// (no landing, summary, or per-module metadata), so they bypass
// buildProethicaModule and map their items straight to session items.
export const buildProethicaTest = (raw: NewFormatModuleItem[]) => {
	const normalizedItems = normalizeModuleItems(raw, {
		overrides: { [PROETHICA_TEST_UI_TYPE]: normalizeKnowledgeCheck },
	});

	return {
		normalizedItems,
		sessionItems: buildSessionItems(normalizedItems, { moduleLabel: "" }),
	};
};
